import { type ReactNode, useRef } from 'react'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
} from 'react-router'
import logoDrawing from '#app/assets/logo-no-background.png'
import { HouseMark } from '#app/components/atoms/HouseMark.tsx'
import { CopyButton } from '#app/components/molecules/CopyButton.tsx'
import { formatPhone } from '#app/utils/help-contact.ts'
import {
	formatClockSpan,
	formatDayMonth,
	formatProgramHeadline,
	formatWeekday,
	getProgramFacts,
} from '#app/domains/program/pure'
import { prisma } from '#app/utils/db.server.ts'
import { getEffectiveMode } from '#app/utils/maintenance.server.ts'
import { SITE_DESCRIPTION, pageMeta } from '#app/utils/page-meta.ts'
import { userHasPermission } from '#app/utils/permissions.server.ts'
import { slugify } from '#app/utils/slug.ts'

export const meta: MetaFunction = (args) =>
	pageMeta(args, {
		title: 'Presse og profil',
		description:
			'Logo, fargar, bilete og pressetekst for Vorrå Huskonsertfestival, fritt til bruk for presse og samarbeidspartnarar.',
	})

const ARRANGOR = 'Vorrå Huskonsert Festival · org.nr 937 219 113'
/** the Facebook cover carries its year's dates */
const COVER = { year: 2026, src: '/promo/facebook-forsidebilete-2026.png' }

/** the site's own colours (tailwind.css), named the way the poster reads */
const COLOURS = [
	{ name: 'Natt', hex: '#121827', use: 'Himmelen bak huset, mørk bakgrunn' },
	{ name: 'Lampe', hex: '#F6D77A', use: 'Lyset i vindauga, utheving på mørkt' },
	{ name: 'Glo', hex: '#D2481A', use: 'Skorsteinen og døra, knappar' },
	{ name: 'Papir', hex: '#F6EFDF', use: 'Lys bakgrunn' },
	{ name: 'Blekk', hex: '#1C1A22', use: 'Tekst og strek' },
]

const rgb = (hex: string) =>
	[1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(' ')

export async function loader({ request }: LoaderFunctionArgs) {
	const festival = await prisma.festival.findFirst({
		where: { isCurrent: true },
		select: {
			year: true,
			datesText: true,
			introText: true,
			mode: true,
			gateReleaseAt: true,
			contactName: true,
			contactPhone: true,
			contactEmail: true,
			events: {
				orderBy: { date: 'asc' },
				select: {
					id: true,
					title: true,
					date: true,
					endsAt: true,
					location: true,
					host: true,
				},
			},
		},
	})
	// dates, texts and the programme only once the festival is out, as on the
	// front page; the logo, colours and contact are for any time of year
	const visible =
		getEffectiveMode(festival) === 'LIVE' ||
		(await userHasPermission(request, 'update:event:any'))

	return {
		festival:
			festival && visible
				? {
						year: festival.year,
						datesText: festival.datesText,
						introText: festival.introText,
						events: festival.events,
					}
				: null,
		contact: {
			name: festival?.contactName ?? null,
			phone: festival?.contactPhone ?? null,
			email: festival?.contactEmail ?? null,
		},
	}
}

export default function PressRoute() {
	const { festival, contact } = useLoaderData<typeof loader>()
	const events = festival?.events ?? []
	const facts = getProgramFacts(events)

	return (
		<main className="pb-32">
			<header className="bg-night text-paper">
				<div className="mx-auto grid max-w-5xl items-center gap-8 px-4 py-12 md:grid-cols-[3fr_2fr]">
					<div>
						<p className="text-lamp mb-3 text-xs font-bold tracking-[0.2em] uppercase">
							Presse og profil
						</p>
						<h1 className="font-display mb-4 text-4xl font-semibold md:text-5xl">
							Vorrå Huskonsertfestival
						</h1>
						<p className="max-w-xl text-lg opacity-85">
							Logo, fargar, bilete og tekst om festivalen. Bruk det fritt når de
							skriv, sender eller deler noko om oss.
						</p>
						<nav
							aria-label="På denne sida"
							className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm"
						>
							{[
								['#om', 'Om festivalen'],
								['#logo', 'Logo'],
								['#fargar', 'Fargar og skrift'],
								['#bilete', 'Bilete'],
								['#kontakt', 'Kontakt'],
							].map(([href, label]) => (
								<a key={href} href={href} className="text-lamp underline">
									{label}
								</a>
							))}
						</nav>
					</div>
					<img
						src={logoDrawing}
						alt="Festivalhuset: eit hus med open dør og ei jente i lyset"
						width={1074}
						height={1411}
						className="mx-auto h-auto max-h-72 w-auto"
					/>
				</div>
			</header>

			<div className="mx-auto flex max-w-5xl flex-col gap-14 px-4 pt-12">
				<section id="om" aria-labelledby="om-tittel" className="scroll-mt-4">
					<h2
						id="om-tittel"
						className="font-display mb-4 text-3xl font-semibold"
					>
						Om festivalen
					</h2>
					<dl className="mb-8 grid gap-x-6 gap-y-3 sm:grid-cols-[10rem_1fr]">
						{festival ? (
							<>
								<dt className="font-semibold">Når</dt>
								<dd>{festival.datesText}</dd>
							</>
						) : null}
						<dt className="font-semibold">Kvar</dt>
						<dd>
							Vorrå-feltet på Karmøy
							{facts.venues ? `, ${facts.venues} stader` : ''}
						</dd>
						{events.length ? (
							<>
								<dt className="font-semibold">Program</dt>
								<dd>{formatProgramHeadline(events)}</dd>
							</>
						) : null}
						<dt className="font-semibold">Billettar</dt>
						<dd>
							Pris etter evne. Barn (12 år og under) går gratis.{' '}
							<a className="link" href="/program">
								huskonsertfestivalen.no
							</a>
						</dd>
						<dt className="font-semibold">Arrangør</dt>
						<dd>{ARRANGOR}</dd>
					</dl>

					<div className="grid gap-6 md:grid-cols-2">
						<PressText title="Kort" text={SITE_DESCRIPTION} />
						{festival?.introText ? (
							<PressText title="Lengre" text={festival.introText} />
						) : null}
					</div>
				</section>

				<section
					id="logo"
					aria-labelledby="logo-tittel"
					className="scroll-mt-4"
				>
					<h2
						id="logo-tittel"
						className="font-display mb-2 text-3xl font-semibold"
					>
						Logo
					</h2>
					<p className="mb-6 max-w-2xl opacity-80">
						Huset med open dør er festivalen. Bruk teikninga der det er plass,
						og husmerket der logoen blir liten. Ikkje strekk dei eller byt
						fargar.
					</p>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						<Asset
							title="Teikninga"
							detail="PNG, gjennomsiktig, 1074 × 1411"
							href={logoDrawing}
							download="huskonsertfestivalen-teikning.png"
						>
							<div className="bg-night flex h-48 items-center justify-center rounded-t-2xl p-4">
								<img
									src={logoDrawing}
									alt=""
									width={1074}
									height={1411}
									className="h-full w-auto"
								/>
							</div>
						</Asset>
						<Asset
							title="Husmerket"
							detail="SVG, vektor – for trykk"
							href="/presse/husmerke.svg"
							download="huskonsertfestivalen-husmerke.svg"
						>
							<div className="bg-paper flex h-48 items-center justify-center rounded-t-2xl p-4">
								<HouseMark className="h-28 w-28" />
							</div>
						</Asset>
						<Asset
							title="App-ikon"
							detail="PNG, 512 × 512"
							href="/favicons/android-chrome-512x512.png"
							download="huskonsertfestivalen-ikon.png"
						>
							<div className="bg-paper flex h-48 items-center justify-center rounded-t-2xl p-4">
								<img
									src="/favicons/android-chrome-512x512.png"
									alt=""
									width={512}
									height={512}
									className="h-28 w-28 rounded-2xl"
								/>
							</div>
						</Asset>
					</div>
				</section>

				<section
					id="fargar"
					aria-labelledby="fargar-tittel"
					className="scroll-mt-4"
				>
					<h2
						id="fargar-tittel"
						className="font-display mb-6 text-3xl font-semibold"
					>
						Fargar og skrift
					</h2>
					<ul className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
						{COLOURS.map((colour) => (
							<li
								key={colour.hex}
								className="border-line overflow-hidden rounded-2xl border"
							>
								<div
									className="h-20"
									style={{ backgroundColor: colour.hex }}
									aria-hidden="true"
								/>
								<div className="flex flex-col gap-1 p-3 text-sm">
									<span className="font-semibold">{colour.name}</span>
									<span className="font-mono">{colour.hex}</span>
									<span className="font-mono text-xs opacity-70">
										RGB {rgb(colour.hex)}
									</span>
									<span className="text-xs opacity-70">{colour.use}</span>
									<CopyButton
										text={colour.hex}
										label="Kopier"
										className="btn btn-xs mt-1 self-start"
									/>
								</div>
							</li>
						))}
					</ul>
					<div className="grid gap-6 md:grid-cols-2">
						<div>
							<p className="font-display text-4xl font-semibold">
								Konsertar i stover og naust
							</p>
							<p className="mt-2 opacity-80">
								Overskrifter er i{' '}
								<a
									className="link"
									href="https://fonts.google.com/specimen/Fraunces"
									target="_blank"
									rel="noreferrer"
								>
									Fraunces
								</a>
								, ein gratis skrift frå Google Fonts.
							</p>
						</div>
						<div className="opacity-80">
							<p>
								Namnet er Vorrå Huskonsertfestival, eller Huskonsertfestivalen
								på Vorrå.
							</p>
							<p className="mt-2">Festivalen skriv nynorsk.</p>
						</div>
					</div>
				</section>

				<section
					id="bilete"
					aria-labelledby="bilete-tittel"
					className="scroll-mt-4"
				>
					<h2
						id="bilete-tittel"
						className="font-display mb-6 text-3xl font-semibold"
					>
						Bilete
					</h2>
					<div className="grid gap-6 md:grid-cols-2">
						<Asset
							title="Festivalkortet"
							detail="PNG, 1200 × 630 – for nettsaker og deling"
							href="/og/huskonsertfestivalen.png"
							download="huskonsertfestivalen.png"
						>
							<img
								src="/og/huskonsertfestivalen.png"
								alt="Festivalkortet: huset på Vorrå i kveldsmørket"
								width={1200}
								height={630}
								className="h-auto w-full rounded-t-2xl"
							/>
						</Asset>
						{festival?.year === COVER.year ? (
							<Asset
								title={`Forsidebilete for Facebook ${festival.year}`}
								detail="PNG, 1640 × 624"
								href={COVER.src}
								download={`huskonsertfestivalen-forsidebilete-${festival.year}.png`}
							>
								<img
									src={COVER.src}
									alt="Forsidebiletet med datoane for festivalen"
									width={1640}
									height={624}
									className="h-auto w-full rounded-t-2xl"
								/>
							</Asset>
						) : null}
					</div>

					{events.length ? (
						<>
							<h3 className="font-display mt-10 mb-4 text-2xl font-semibold">
								Programmet
							</h3>
							<ul className="grid gap-6 md:grid-cols-2">
								{events.map((event) => (
									<li key={event.id}>
										<Asset
											title={event.title}
											detail={`${formatWeekday(event.date)} ${formatDayMonth(event.date)} kl. ${formatClockSpan(event.date, event.endsAt)}${event.location ? ` · ${event.location}` : ''}`}
											href={`/events/${event.id}/delingsbilete.jpg`}
											download={`huskonsertfestivalen-${slugify(event.title)}.jpg`}
										>
											<img
												src={`/events/${event.id}/delingsbilete.jpg`}
												alt=""
												width={1200}
												height={630}
												loading="lazy"
												className="h-auto w-full rounded-t-2xl"
											/>
										</Asset>
									</li>
								))}
							</ul>
						</>
					) : null}
					<p className="mt-6 max-w-2xl opacity-80">
						Treng de pressebilete av artistane eller ein plakat på papir, ta
						kontakt.
					</p>
				</section>

				<section
					id="kontakt"
					aria-labelledby="kontakt-tittel"
					className="scroll-mt-4"
				>
					<h2
						id="kontakt-tittel"
						className="font-display mb-4 text-3xl font-semibold"
					>
						Kontakt
					</h2>
					<ul className="flex flex-col gap-2">
						{contact.name ? (
							<li className="font-semibold">{contact.name}</li>
						) : null}
						{contact.phone ? (
							<li>
								Telefon:{' '}
								<a
									className="link"
									href={`tel:${contact.phone.replace(/\s/g, '')}`}
								>
									{formatPhone(contact.phone)}
								</a>
							</li>
						) : null}
						{contact.email ? (
							<li>
								E-post:{' '}
								<a className="link" href={`mailto:${contact.email}`}>
									{contact.email}
								</a>
							</li>
						) : null}
						<li className="opacity-80">{ARRANGOR}</li>
					</ul>
				</section>
			</div>
		</main>
	)
}

/** A text to lift into an article as it is. */
function PressText({ title, text }: { title: string; text: string }) {
	const ref = useRef<HTMLParagraphElement>(null)
	return (
		<div className="border-line bg-sheet flex flex-col gap-3 rounded-2xl border p-5">
			<h3 className="font-semibold">{title}</h3>
			<p ref={ref} className="whitespace-pre-line">
				{text}
			</p>
			<CopyButton
				text={text}
				selectRef={ref}
				className="btn btn-sm mt-auto self-start"
			/>
		</div>
	)
}

/** A picture or file to take away: preview, what it is, and the download. */
function Asset({
	title,
	detail,
	href,
	download,
	children,
}: {
	title: string
	detail: string
	href: string
	download: string
	children: ReactNode
}) {
	return (
		<figure className="border-line bg-sheet flex h-full flex-col rounded-2xl border">
			{children}
			<figcaption className="flex flex-1 flex-col gap-1 p-4">
				<span className="font-semibold">{title}</span>
				<span className="text-sm opacity-70">{detail}</span>
				<a
					href={href}
					download={download}
					className="btn btn-sm mt-2 self-start"
				>
					Last ned
				</a>
			</figcaption>
		</figure>
	)
}
